import moment from 'moment';
import momentTimezone from 'moment-timezone';
import map from 'lodash/map';
import isNil from 'lodash/isNil';
import includes from 'lodash/includes';
import { formatDistanceToNow } from 'date-fns';

export const getCurrentUtcTime = (formatted = true) => {
  const time = moment.utc();

  if (formatted) {
    return time.format();
  }

  return time;
};

export const getStartOfMonth = () => moment().startOf('month').format('YYYY-MM-DD');

const getLocalTimeZone = () => {
  if (typeof window.nex_localTimeZone === 'undefined') {
    // console.log('detecting timezone');
    window.nex_localTimeZone = momentTimezone.tz.guess();
  }
  return window.nex_localTimeZone;
};

const getTimeZones = () => {
  return map(momentTimezone.tz.names(), (z) => {
    return { label: z, value: z };
  });
};

export const getLocalTimeZoneLabel = () => {
  return getDateString(Date.now(), 'z');
};

export const convertUTCToLocal = (timestamp) => {
  return momentTimezone
    .tz(moment(timestamp).format('YYYY-MM-DD HH:mm:ss'), 'UTC')
    .tz(getLocalTimeZone())
    .valueOf();
};

export function getDateAgoString(dateString) {
  let toTimeZone = getLocalTimeZone();
  let dateToConvert = !includes(dateString, 'Z') ? `${date}Z` : dateString;
  const date = momentTimezone.tz(dateToConvert, toTimeZone);
  const currentTime = moment();

  // Calculate the difference between the current time and the provided date
  const diff = moment.duration(currentTime.diff(date));

  // Check if the date is in the past or future
  const isInPast = date.isBefore(currentTime);

  // Check if the date is within the last minute
  if (diff.asSeconds() < 60) {
    return isInPast ? `${diff.seconds()}s ago` : `in ${diff.seconds()}s`;
  }

  // Check if the date is within the last hour
  if (diff.asMinutes() < 60) {
    return isInPast ? `${diff.minutes()}m ago` : `in ${diff.minutes()}m`;
  }

  // Check if the date is within the last day
  if (diff.asHours() < 24) {
    return isInPast ? `${diff.hours()}h ago` : `in ${diff.hours()}h`;
  }

  // Check if the date is within the current year
  if (date.isSame(currentTime, 'year')) {
    // Check if the date is yesterday
    if (date.isSame(currentTime.subtract(1, 'days'), 'day')) {
      return date.format('[Yesterday], h:mm a');
    }

    // Return the date in the format "MMM D, h:mm a" (e.g. "Jan 1, 12:00 am")
    return date.format('MMM D, h:mm a');
  }

  // Return the date in the format "MMM D, YYYY" (e.g. "Jan 1, 2021")
  return date.format('MMM D, YYYY');
}

export const getDateString = (date, toFormat = 'MMM D, YYYY h:mma z') => {
  let toTimeZone = getLocalTimeZone();
  return date ? momentTimezone.tz(date, toTimeZone).format(toFormat).toString() : null;
};

export const convertDateStringToFormat = (
  date,
  toFormat = 'MMM D, YYYY [at] h:mma z',
  toLocal = true,
  isUnixTsNum = false,
  isUnixSeconds = false,
) => {
  if (isNil(date)) {
    return null;
  }

  let dateString = date;

  if (isUnixTsNum === true) {
    dateString = isUnixSeconds ? moment.unix(date).toISOString() : moment(date).toISOString();
  }
  let toTimeZone = getLocalTimeZone();
  let dateToConvert = toLocal === true && !includes(dateString, 'Z') ? `${date}Z` : dateString;

  return toLocal
    ? momentTimezone.tz(dateToConvert, toTimeZone).format(toFormat)
    : moment.utc(dateToConvert).format(toFormat);
};

export const getPastDateRange = (
  numDays = 7,
  endDateFormat = null,
  startDateFormat = 'YYYY-MM-DD',
  inUTC = true,
) => {
  if (numDays < 0) {
    throw Error('Cannot request negative date range');
  }

  let dateRange = {};
  let endDate = inUTC ? moment.utc() : moment();
  let startDate = inUTC ? moment.utc().subtract(numDays, 'd') : moment().subtract(numDays, 'd');

  dateRange.end = endDate.format(endDateFormat);
  dateRange.start = startDate.format(startDateFormat);
  return dateRange;
};

const getTimeDifference = (fromTimeString, toTimeString, utcTimes = true) => {
  let startMoment = moment(fromTimeString);
  return startMoment.diff(toTimeString);
};

const timeAgoFromNow = (fromTimeString = null) => {
  return fromTimeString ? moment().diff(fromTimeString) : fromTimeString;
};

export const getLastNDays = (
  numDays = 7,
  dateFormat = 'MM/DD/YY',
  ascending = true,
  inUTC = true,
) => {
  if (numDays < 0) {
    throw Error('Cannot request for negative date range');
  }

  var dates = [];

  var start = inUTC ? moment.utc().subtract(numDays, 'd') : moment().subtract(numDays, 'd');

  dates.push(start.format(dateFormat));

  for (var i = 0; i < numDays; i++) {
    dates.push(start.add(1, 'd').format(dateFormat));
  }

  if (!ascending) {
    return dates.reverse();
  }
  return dates;
};

function showTimeSince(timestamp, { addSuffix = true } = {}) {
  if (!timestamp) return null;
  if (isNaN(timestamp)) return null;
  if (typeof timestamp !== 'number') return null;

  const date = new Date(timestamp);
  let timeAgo = formatDistanceToNow(date, { addSuffix });

  // Remove "about " prefix if present
  if (timeAgo.startsWith('about ')) {
    timeAgo = timeAgo.substring(6);
  }

  return timeAgo;
}

export const getTimezoneAbbr = () => {
  try {
    const tzAbbr = new Intl.DateTimeFormat('en', {
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    return tzAbbr || '';
  } catch (error) {
    return '';
  }
};

export default {
  convertUTCToLocal,
  convertDateStringToFormat,
  getLastNDays,
  getPastDateRange,
  getTimeZones,
  getLocalTimeZoneLabel,
  getTimeDifference,
  getDateString,
  timeAgoFromNow,
  getStartOfMonth,
  getCurrentUtcTime,
  showTimeSince,
};
